import { useState, useEffect, useCallback } from 'react';
import { apiFetch, ApiError } from './client';
import { encodePathSegment } from '../lib/url';

// --- Generic hook ---

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /**
   * HTTP status code of the most recent failure, or null if the last call
   * succeeded / hasn't completed. Lets consumers branch on 403 to render a
   * restricted-access message, on 5xx to offer a retry, etc.
   */
  errorStatus: number | null;
  refetch: () => void;
}

export function useApi<T>(path: string | null, deps: unknown[] = []): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const fetchData = useCallback(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    apiFetch<T>(path)
      .then(setData)
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setErrorStatus(e instanceof ApiError ? e.status : null);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, errorStatus, refetch: fetchData };
}

/**
 * Safely extract an array from an API response that may be:
 * - an array directly
 * - an object with a known array property (items, data, results, etc.)
 * - null/undefined
 */
function extractArray<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    for (const key of ['items', 'data', 'results', 'records', 'entries', 'schemas', 'credentials', 'history', 'issuers', 'trustedIssuers', 'events', 'agents']) {
      if (Array.isArray(obj[key])) return obj[key] as T[];
    }
  }
  return [];
}

/** Wrapper around useApi for endpoints expected to return arrays */
export function useApiArray<T>(path: string | null, deps: unknown[] = []): UseApiResult<T[]> {
  const result = useApi<unknown>(path, deps);
  const data = result.data != null ? extractArray<T>(result.data) : null;
  return { ...result, data } as UseApiResult<T[]>;
}

// --- Dashboard ---

/**
 * A dashboard stat card. `direction` drives the icon and colour; `trend` is
 * the label. Trends compare the last 7 days with the 7 before that.
 *
 *   up / down  — previous window non-zero, a real percentage
 *   new        — activity this window, none before: a percentage is undefined
 *   flat       — zero in both windows
 *   none       — the API reports no trend for this stat
 */
type TrendDirection = 'up' | 'down' | 'flat' | 'new' | 'none';
// trendNote spells out the comparison behind the badge. The badge sits next to a
// running total but measures something else — items created this week versus
// last — so without the counts "80 DIDs, -100%" reads as every DID vanishing.
type StatCard = { label: string; value: string; trend: string; direction: TrendDirection; icon: string; trendNote: string };

type TrendDetail = { recent: number; previous: number; pct: number | null };

function describeTrend(detail: TrendDetail | undefined): { trend: string; direction: TrendDirection; trendNote: string } {
  if (!detail) return { trend: '', direction: 'none', trendNote: '' };
  return { ...trendBadge(detail), trendNote: `${detail.recent} added in the last 7 days · ${detail.previous} in the 7 days before` };
}

function trendBadge(detail: TrendDetail): { trend: string; direction: TrendDirection } {
  if (detail.pct === null) {
    // Nothing in the comparison window. Previously reported as "+100%",
    // which reads as growth when the honest label is "new".
    return detail.recent > 0
      ? { trend: `+${detail.recent} new`, direction: 'new' }
      : { trend: '—', direction: 'flat' };
  }
  const pct = Math.round(detail.pct);
  if (pct === 0) return { trend: '0%', direction: 'flat' };
  return { trend: `${pct > 0 ? '+' : ''}${pct}%`, direction: pct > 0 ? 'up' : 'down' };
}

export function useDashboardStats() {
  const result = useApi<Record<string, unknown>>('/dashboard/stats');
  const d = result.data;
  const details = (d?.trendDetails ?? {}) as Record<string, TrendDetail>;

  const card = (label: string, key: string, icon: string): StatCard => ({
    label,
    value: String(d?.[key] ?? 0),
    icon,
    ...describeTrend(details[key]),
  });

  // Transform flat stats object into array of stat cards for the dashboard grid
  const cards: StatCard[] | null = d ? [
    card('Total DIDs', 'totalDIDs', 'badge'),
    card('Total Credentials', 'totalCredentials', 'verified'),
    card('Total Agents', 'totalAgents', 'smart_toy'),
    card('Total Schemas', 'totalSchemas', 'schema'),
    card('Active Delegations', 'activeDelegations', 'handshake'),
    card('Total Issuers', 'totalIssuers', 'verified_user'),
  ] : null;
  return { ...result, data: cards };
}

/**
 * Renders a timestamp the way a person reads one.
 *
 * The API returns RFC-3339 strings, which were previously passed straight to
 * the UI — a dashboard showing "2026-08-23T09:59:25.431158796Z" tells the
 * reader almost nothing at a glance. Recent events become relative ("4m ago"),
 * older ones fall back to a localized date so they stay unambiguous.
 */
export function formatEventTime(raw: unknown): string {
  const value = String(raw ?? '').trim();
  if (!value) return '';

  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return value; // not a date — show as-is

  const seconds = Math.floor((Date.now() - then.getTime()) / 1000);
  if (seconds < 0) return then.toLocaleString();   // clock skew or future-dated
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return then.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const EVENT_ICONS: Record<string, string> = {
  did: 'badge',
  credential: 'verified',
  schema: 'schema',
  agent: 'smart_toy',
  zk: 'shield_lock',
};

export function useNetworkEvents() {
  const result = useApi<Record<string, unknown>[] | Record<string, unknown>>('/dashboard/events?limit=8');
  // The API sends {events: [{id, type, title, description, createdAt}]}.
  const events = result.data
    ? (Array.isArray(result.data) ? result.data : (result.data as Record<string, unknown>).events as Record<string, unknown>[] ?? []).map((e: Record<string, unknown>, i: number) => ({
        id: String(e.id ?? i),
        label: String(e.title ?? e.type ?? ''),
        value: String(e.description ?? ''),
        time: formatEventTime(e.createdAt ?? e.created_at),
        timestamp: String(e.createdAt ?? e.created_at ?? ''),
        icon: EVENT_ICONS[String(e.type)] ?? 'event',
      }))
    : null;
  return { ...result, data: events };
}

export function useRecentActivity(limit?: number) {
  const result = useApi<Record<string, unknown>>(`/dashboard/activity${limit ? `?limit=${limit}` : ''}`);
  const raw = result.data;
  const arr = raw ? (Array.isArray(raw) ? raw : Array.isArray((raw as Record<string, unknown>).activity) ? (raw as Record<string, unknown>).activity as Record<string, unknown>[] : []) : null;
  const activities = arr ? arr.map((a: Record<string, unknown>) => ({
    action: String(a.action ?? ''),
    detail: String(a.details ?? a.detail ?? a.description ?? ''),
    // Who performed it. Only the platform-wide (admin) feed spans multiple
    // users, so the Dashboard shows this for admins.
    actor: String(a.actor ?? ''),
    time: formatEventTime(a.timestamp ?? a.time ?? a.created_at),
    timestamp: String(a.timestamp ?? a.time ?? a.created_at ?? ''),
    icon: 'history',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  })) : null;
  return { ...result, data: activities };
}

// --- Trust Explorer / Issuers ---

/** Network-wide trust figures for the Trust Explorer. Never scoped to the caller. */
export function useNetworkStats() {
  return useApi<{
    totalDIDs: number;
    totalAgents: number;
    totalCredentials: number;
    totalRevoked: number;
    totalSchemas: number;
    totalIssuers: number;
    totalZKProofs: number;
    trustSignals: number;
    scoredAgents: number;
    revocationRate: number;
    agentsByLevel: Record<string, number>;
  }>('/dashboard/network-stats');
}

// Shape matches what GET /issuers actually returns. It previously declared
// category/icon/iconBg/status/zk, none of which the API sends — so every issuer
// rendered as permanently "pending" with a blank category and grey ZK icons.
export function useIssuers() {
  return useApiArray<{
    did: string;
    name: string;
    totalIssued: number;
    active: number;
    revoked: number;
    firstIssued: string | null;
    lastIssued: string | null;
  }>('/issuers');
}

// --- DIDs ---

export function useMyDIDs() {
  // mine=true: the caller's own DIDs even for an admin, who otherwise gets every
  // DID on the platform and would appear to own other users' identities.
  const result = useApi<unknown>('/dids?mine=true');
  const items = result.data ? extractArray<Record<string, unknown>>(result.data) : null;

  // GET /dids returns DID documents only, with no credential count, so the
  // tally is fetched separately. It must be one filtered request per DID:
  // GET /credentials applies no caller scoping at all — an unfiltered call
  // returns the whole platform's credentials, which would count other
  // holders' credentials against this user's DIDs. The count was previously
  // hardcoded to 0, so every card read "0 credentials" regardless.
  const didIds = (items ?? []).map((d) => String(d.id ?? '')).filter(Boolean);
  const didKey = didIds.join(',');
  const [countsBySubject, setCountsBySubject] = useState<Record<string, number>>({});

  useEffect(() => {
    if (didIds.length === 0) {
      setCountsBySubject({});
      return;
    }
    let cancelled = false;
    Promise.all(
      didIds.map(async (did) => {
        try {
          const res = await apiFetch<unknown>(`/credentials?subject=${encodePathSegment(did)}&limit=100`);
          const list = extractArray<unknown>(res);
          // Prefer the server's total when present — the list is capped at 100.
          const total = (res as { total?: unknown })?.total;
          return [did, typeof total === 'number' ? total : list.length] as const;
        } catch {
          return [did, 0] as const;   // a failed count must not blank the card
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setCountsBySubject(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
    // didKey collapses the id list into a stable dependency.

  }, [didKey]);
  const mapped = items ? items.map((d) => ({
    did: String(d.id ?? ''),
    label: String(d.id ?? '').split(':').pop()?.slice(0, 12) + '...' || 'DID',
    keyType: Array.isArray(d.verificationMethod) && d.verificationMethod.length > 0
      ? String((d.verificationMethod as Record<string, unknown>[])[0].type ?? 'Ed25519')
        .replace('VerificationKey2020', '')
      : 'Ed25519',
    created: String(d.created ?? d.updated ?? ''),
    status: d.deactivated ? 'deactivated' : 'active',
    credentials: countsBySubject[String(d.id ?? '')] ?? 0,
  })) : null;
  return { ...result, data: mapped };
}

// --- DID Detail ---

export function useDIDDetail(did: string) {
  return useApi<Record<string, unknown>>(did ? `/dids/${did}` : null);
}

export function useDIDDelegates(did: string) {
  return useApiArray<{
    id: number;
    did: string;
    delegateType: string;
    delegate: string;
    validTo: string;
    createdAt: string;
  }>(did ? `/dids/${did}/delegates` : null);
}

export function useResolveDID() {
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(async (did: string) => {
    if (!did) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<Record<string, unknown>>(`/dids/resolve/${encodePathSegment(did)}`);
      setResult(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Resolution failed');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data: result, loading, error, resolve };
}

// --- Agents ---

export function useAgentStats() {
  const result = useApi<Record<string, unknown>>('/agents/me/stats');
  const cards = result.data ? [
    { label: 'Total Agents', value: String(result.data.totalAgents ?? 0), sub: 'Registered agents', icon: 'smart_toy' },
    { label: 'Avg Trust', value: `${Math.round(((result.data.avgTrustScore as number) ?? 0) * 100)}%`, sub: 'Network average', icon: 'shield' },
    { label: 'Active Delegations', value: String(result.data.activeDelegations ?? 0), sub: 'Capability delegations', icon: 'handshake' },
  ] : null;
  return { ...result, data: cards };
}

export function useAgents() {
  // include=decommissioned keeps retired agents in the list so their status is
  // visible; without it they silently disappeared after decommissioning.
  const result = useApiArray<Record<string, unknown>>('/agents/me?limit=50&include=decommissioned');
  const mapped = result.data ? result.data.map((raw) => {
    const modelInfo = (raw.modelInfo ?? {}) as Record<string, string>;
    const autonomyNum = typeof raw.autonomyLevel === 'number' ? raw.autonomyLevel : 0;
    const trustNum = typeof raw.trustScore === 'number' ? raw.trustScore : 0;
    const caps = Array.isArray(raw.capabilities) ? raw.capabilities : [];
    return {
      name: modelInfo.name || String(raw.operator || '') || String(raw.id || '').split(':').pop()?.slice(0, 12) || 'Agent',
      category: caps.length > 0 ? String((caps[0] as Record<string, string>).name || '') : '',
      did: String(raw.id ?? ''),
      key: String(raw.id ?? '').split(':').pop()?.slice(0, 12) || '',
      autonomy: autonomyNum >= 4 ? 'Principal' : autonomyNum >= 3 ? 'Senior' : autonomyNum >= 2 ? 'Junior' : 'Intern',
      trust: trustNum <= 1 ? Math.round(trustNum * 100) : trustNum,
      // A deactivated agent has been decommissioned (a permanent retirement);
      // a suspended one is paused and can be reactivated.
      status: raw.deactivated ? 'decommissioned' : raw.suspended ? 'suspended' : 'active',
    };
  }) : null;
  return { ...result, data: mapped };
}

export function useMarketplaceAgents() {
  return useApiArray<Record<string, unknown>>('/agents/discover?limit=20');
}

interface AgentDetailData {
  name: string;
  did: string;
  status: string;
  operator: string;
  operatorName: string;
  /** Why the agent is suspended, when it is. */
  suspensionReason?: string;
  deployedDate: string;
  modelProvider: string;
  modelName: string;
  trustScore: number;
  trustPercentile: number;
  autonomyLevel: string;
  autonomyRank: number;
  capabilities: { name: string; description: string; version?: string }[];
}

export function useAgentDetail(did: string) {
  const result = useApi<Record<string, unknown>>(did ? `/agents/${did}` : null);
  const mapped: AgentDetailData | null = (() => {
    if (!result.data) return null;
    // API may return { agent: {...}, auditLog, delegations } or the agent directly
    const raw = (result.data.agent ?? result.data) as Record<string, unknown>;
    const modelInfo = (raw.modelInfo ?? {}) as Record<string, string>;
    const autonomy = typeof raw.autonomyLevel === 'number' ? raw.autonomyLevel as number : 0;
    const trust = typeof raw.trustScore === 'number' ? raw.trustScore as number : 0;
    const operatorDid = (raw.operator as string) || '';
    const createdRaw = (raw.deployedDate as string) || (raw.created as string) || '';
    const deployedDate = createdRaw ? new Date(createdRaw).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
    const autonomyMap: Record<number, string> = { 0: 'Intern', 1: 'Junior', 2: 'Senior', 3: 'Principal' };
    const autonomyName = typeof raw.autonomyLevel === 'string' ? raw.autonomyLevel : (autonomyMap[autonomy] ?? 'Intern');
    return {
      name: (raw.name as string) || modelInfo.name || (raw.id as string) || '',
      did: (raw.id as string) || (raw.did as string) || did,
      status: (raw.status as string) || (raw.deactivated ? 'decommissioned' : raw.suspended ? 'suspended' : 'active'),
      operator: operatorDid,
      operatorName: (raw.operatorName as string) || '',
      suspensionReason: (raw.suspensionReason as string) || undefined,
      deployedDate,
      modelProvider: (raw.modelProvider as string) || modelInfo.provider || '',
      modelName: (raw.modelName as string) || modelInfo.name || '',
      trustScore: trust <= 1 ? Math.round(trust * 100) : trust,
      trustPercentile: (raw.trustPercentile as number) ?? 0,
      autonomyLevel: autonomyName,
      autonomyRank: (raw.autonomyRank as number) ?? autonomy,
      capabilities: Array.isArray(raw.capabilities)
        ? raw.capabilities.map((c: unknown) => {
            if (typeof c === 'string') return { name: c, description: '' };
            const obj = c as Record<string, string>;
            return { name: obj.name || '', description: obj.description || '', version: obj.version };
          })
        : [],
    };
  })();
  return { ...result, data: mapped };
}

export function useAgentDelegations(did: string) {
  return useApiArray<Record<string, unknown>>(did ? `/agents/${did}/delegations` : null);
}

export function useAgentAuditLog(did: string) {
  return useApiArray<Record<string, unknown>>(did ? `/agents/${did}/audit-log` : null);
}

export function useAgentCredentials(did: string) {
  return useApiArray<Record<string, unknown>>(did ? `/agents/${did}/credentials` : null);
}

export function useAgentDelegationChain(did: string) {
  return useApiArray<Record<string, unknown>>(did ? `/agents/${did}/delegation-chain` : null);
}

// --- Schemas ---

/**
 * Credential schemas.
 *
 * Pass an author DID to get only that author's schemas — what "My Schemas"
 * means. Without it the endpoint returns every issuer's schemas, which is the
 * network-wide view. Both pages previously called this unfiltered, so "My
 * Schemas" listed other issuers' schemas too.
 *
 * Pass `null` to skip the request entirely (e.g. while the caller's own DID is
 * still resolving) so the unfiltered list is never fetched by mistake.
 */
/**
 * List credential schemas. `author` limits to one DID; 'mine' limits to every
 * DID the signed-in user owns; null skips the request.
 */
export function useSchemas(author?: string | null) {
  const path =
    author === null
      ? null
      : author === 'mine'
        ? '/credentials/schemas?mine=true&limit=100'
        : author
          ? `/credentials/schemas?author=${encodePathSegment(author)}`
          : '/credentials/schemas';
  const result = useApiArray<Record<string, unknown>>(path, [author]);
  const mapped = result.data ? result.data.map((s) => {
    // Derive fields from schema.properties if fields not present
    let fields = s.fields as { name: string; type: string; required: boolean }[] | undefined;
    if (!fields && s.schema && typeof s.schema === 'object') {
      const props = (s.schema as Record<string, unknown>).properties as Record<string, { type?: string }> | undefined;
      const req = (s.schema as Record<string, unknown>).required as string[] | undefined;
      if (props) {
        fields = Object.entries(props).map(([name, def]) => ({
          name,
          type: def?.type ?? 'string',
          required: req?.includes(name) ?? false,
        }));
      }
    }
    return {
      id: String(s.id ?? ''),
      name: String(s.name ?? ''),
      version: String(s.version ?? ''),
      author: s.author as string | undefined,
      authorName: s.authorName as string | undefined,
      description: s.description as string | undefined,
      fields: fields ?? [],
      credentials: s.credentials as number | undefined,
      credentialCount: s.credentialCount as number | undefined,
      updated: s.updated as string | undefined,
      createdAt: s.createdAt as string | undefined,
      deactivated: Boolean(s.deactivated),
      // The API is the source of truth via `deactivated`; a retired schema can
      // no longer back new credentials.
      status: s.deactivated ? 'inactive' : String(s.status ?? 'active'),
    };
  }) : null;
  return { ...result, data: mapped };
}

// --- Credentials ---

// Pass `null` to skip the request entirely (e.g. while the caller's own DID is
// still resolving). Passing no filter at all fetches every credential the
// caller is allowed to see, so scoped callers must supply a filter or null
// rather than undefined.
export function useCredentials(filters?: string | null) {
  return useApiArray<{
    id: string;
    schema: string;
    holder: string;
    issuedAt: string;
    status: 'active' | 'revoked' | 'suspended';
    revokedAt?: string;
    reason?: string;
  }>(filters === null ? null : `/credentials${filters ? `?${filters}` : ''}`);
}

export function useRecentIssuances(issuerDid?: string) {
  const url = issuerDid
    ? `/credentials?issuer=${encodeURIComponent(issuerDid)}&limit=5`
    : null;
  const result = useApi<{ credentials: Record<string, unknown>[]; total: number }>(url);
  const items = result.data?.credentials ?? [];
  const mapped = items.map((vc) => ({
    id: String(vc.id ?? ''),
    schema: Array.isArray(vc.type) ? String((vc.type as string[]).find((t) => t !== 'VerifiableCredential') ?? '') : '',
    holder: String((vc.credentialSubject as Record<string, unknown>)?.id ?? ''),
    issuedAt: String(vc.issuanceDate ?? ''),
    status: vc.revoked ? 'revoked' : 'active',
  }));
  return { ...result, data: mapped };
}

// --- Verifications ---

export function useVerificationHistory() {
  return useApiArray<{
    id: string;
    credentialType?: string;
    timestamp?: string;
    holderDid?: string;
    issuerDid?: string;
    issuer?: string;
    issuerName?: string;
    holder?: string;
    verifiedAt?: string;
    result: string;
    details?: string;
    /** Phase-7-A: present when the verification was over a ZK proof. */
    proofId?: string;
    /** Phase-7-A: server-joined anchor record (preferred over per-row lookup). */
    anchor?: {
      status: 'pending' | 'confirmed' | 'revoked' | 'failed';
      blockNumber?: number | null;
      revokedBlock?: number | null;
      txHash?: string;
    } | null;
  }>('/verifications/history');
}

// --- Issuer Analytics ---

// Issuer analytics are keyed by the issuer's DID. The API has no `current`
// alias — passing the literal string matches zero rows and renders a blank
// page — so callers must supply a real DID (see useMyDIDs). When the DID is
// not known yet the path is null, which makes useApi skip the request instead
// of firing a query that can never match.
interface IssuerAnalyticsRaw {
  totalIssued?: number;
  active?: number;
  revoked?: number;
  monthlyBreakdown?: { month: string; count: number }[];
  topSchemas?: { name: string; count: number }[];
}

export interface IssuerAnalyticsView {
  stats: { label: string; value: string; trend: string; icon: string }[];
  monthlyData: { month: string; value: number }[];
  topSchemas: { name: string; issued: number; percentage: number }[];
  recentActivity: { action: string; detail: string; time: string; icon: string; iconBg: string; iconColor: string }[];
}

/**
 * The API returns raw counts (totalIssued/active/revoked/monthlyBreakdown), but
 * the page renders stat cards, a bar chart and ranked schemas. Map between the
 * two here — the page previously destructured fields the API never sent, so
 * topSchemas[].issued was undefined and rendering threw, blanking the page.
 */
export function useIssuerAnalytics(did?: string) {
  const result = useApi<IssuerAnalyticsRaw>(did ? `/issuers/${encodePathSegment(did)}/analytics` : null);
  const raw = result.data;

  const mapped: IssuerAnalyticsView | null = raw
    ? (() => {
        const totalIssued = raw.totalIssued ?? 0;
        const active = raw.active ?? 0;
        const revoked = raw.revoked ?? 0;
        const revocationRate = totalIssued > 0 ? Math.round((revoked / totalIssued) * 1000) / 10 : 0;
        return {
          stats: [
            { label: 'Total Issued', value: totalIssued.toLocaleString(), trend: '', icon: 'workspace_premium' },
            { label: 'Active', value: active.toLocaleString(), trend: '', icon: 'check_circle' },
            { label: 'Revoked', value: revoked.toLocaleString(), trend: '', icon: 'block' },
            { label: 'Revocation Rate', value: `${revocationRate}%`, trend: '', icon: 'percent' },
          ],
          monthlyData: (raw.monthlyBreakdown ?? []).map((m) => ({ month: m.month, value: m.count ?? 0 })),
          topSchemas: (raw.topSchemas ?? []).map((s) => ({
            name: s.name,
            issued: s.count ?? 0,
            percentage: totalIssued > 0 ? Math.round(((s.count ?? 0) / totalIssued) * 100) : 0,
          })),
          // The analytics endpoint does not return an activity list; the
          // dashboard feed covers that, so render the section empty rather than
          // crashing on undefined.
          recentActivity: [],
        };
      })()
    : null;

  return { ...result, data: mapped };
}

// --- Revocations ---

export function useRevocationHistory() {
  return useApiArray<{
    id: string;
    credentialId: string;
    action: string;
    reason: string;
    timestamp: string;
    actor: string;
  }>('/revocations/history');
}

// --- Verifier ---

export function useTrustedIssuers() {
  return useApiArray<{
    did: string;
    name: string;
    type: string;
    trustScore: number;
    schemas: string[];
    addedAt: string;
  }>('/verifier/trusted-issuers');
}

export function useSchemaIssuerMapping() {
  return useApiArray<{
    schema: string;
    issuers: string[];
  }>('/verifier/schema-issuer-mapping');
}

export function useComplianceVCs() {
  return useApiArray<{
    type: string;
    required: boolean;
    description: string;
  }>('/verifier/compliance-vcs');
}

// --- Role Applications ---

export interface RoleApplication {
  id: string;
  userId: string;
  requestedRole: 'issuer' | 'verifier';
  status: 'pending' | 'approved' | 'rejected';
  orgName: string;
  orgType: string;
  website?: string;
  purpose: string;
  reviewerId?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  issuedVcId?: string;
  createdAt: string;
  updatedAt: string;
}

export function useMyRoleApplications() {
  return useApiArray<RoleApplication>('/auth/role-applications/me');
}

export function useAdminRoleApplications(status?: string) {
  const qs = status ? `?status=${status}` : '';
  return useApiArray<RoleApplication>(`/admin/role-applications${qs}`);
}
